import { computed, ref, watch } from "vue";
import { useStore } from "vuex";
import { getProperty } from "@/lib/helpers";

import { useBreakpoints } from "@/composables/useBreakpoints";

const FLAG = "mobile_web_optimization";

/**
 * The single gate for rendering a Seranova Drawer as a bottom sheet.
 *
 * Threshold is the Tailwind `md` breakpoint via `useBreakpoints`, matching the
 * reschedule notify toggle (AppointmentDetails/ScheduledTimeForm.vue). The two
 * appear over the same range, so a sheet never shows a toggle the surrounding
 * layout has already dropped.
 *
 * `flagsResolved` is the third state that `useFeatureFlag` cannot express: it
 * returns false both for "flag is off" and "the tenant fetch has not answered
 * yet", so a consumer keyed on it alone is guaranteed a disabled-branch first
 * paint that is then torn down. That is not cosmetic here — the disabled branch
 * for a calendar event is the jQuery/Bootstrap Modal, which imperatively shows
 * itself and registers a backdrop on mount, so mounting and discarding it
 * orphans that backdrop. Consumers whose two branches differ by more than a CSS
 * class must therefore render neither until `flagsResolved` is true.
 * components/FeatureFlag.vue carries the same gate for the same reason.
 */
export function useSheetPlacement() {
  const store = useStore();
  const { type } = useBreakpoints();

  // Tracks the fetch having SETTLED rather than succeeded: `fetchTenant` has no
  // rejection handler, so on a failed request `tenant` stays null forever and a
  // gate keyed on the payload alone would never open.
  const fetchSettled = ref(false);

  if (store.state.tenant) {
    fetchSettled.value = true;
  } else if (!store.state.fetchingTenant) {
    store.dispatch("fetchTenant");
  }

  // `fetchTenant` clears `fetchingTenant` in a `finally`, so the falling edge is
  // the one signal that covers both outcomes — including for a caller that did
  // not start the request itself.
  watch(
    () => store.state.fetchingTenant,
    (isFetching, wasFetching) => {
      if (wasFetching && !isFetching) fetchSettled.value = true;
    }
  );

  const flagsResolved = computed(
    () =>
      !!getProperty(store.state.tenant, "feature_flags") || fetchSettled.value
  );

  const flagEnabled = computed(() => {
    const flags = getProperty(store.state.tenant, "feature_flags");
    // The jbuilder view omits the key entirely when the flag is off, so a
    // missing key and an explicit false are the same answer.
    return flags ? !!flags[FLAG] : false;
  });

  const isSheet = computed(
    () => flagsResolved.value && flagEnabled.value && type.value === "mobile"
  );

  const placement = computed<"right" | "bottom">(() =>
    isSheet.value ? "bottom" : "right"
  );

  return { isSheet, placement, flagsResolved };
}
